import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Send, Lock, X, CheckCircle } from 'lucide-react';
import { C } from '../constants';
import { setMeta } from '../utils/meta';
import { supabase } from '../supabase';

// ── Notification recipients ───────────────────────────────────────────────
// First email is the primary recipient (appears in FormSubmit URL).
// All others are CC'd automatically on every submission.
// Add or remove emails here as needed.
const NOTIFY_EMAILS = [
  'bkarun479@gmail.com',   // primary
  'balajirrb@outlook.com', // CC
];
const FORMSUBMIT_URL = `https://formsubmit.co/ajax/${NOTIFY_EMAILS[0]}`;

const INPUT_STYLE = {
  background:   '#0a0a1a',
  border:       `1px solid rgba(255,255,255,0.08)`,
  borderRadius: 10,
  padding:      '13px 16px',
  fontFamily:   "'Poppins', sans-serif",
  fontSize:     '0.92rem',
  color:        C.white,
  outline:      'none',
  width:        '100%',
  transition:   'border-color 200ms ease, box-shadow 200ms ease',
};

const LABEL_STYLE = {
  fontSize:      '0.72rem',
  fontWeight:    700,
  color:         C.textDim,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  marginBottom:  6,
  display:       'block',
};

const REASONS = [
  { value: 'feedback',    label: 'General feedback'      },
  { value: 'bug',         label: 'Report a bug'          },
  { value: 'feature',     label: 'Feature request'       },
  { value: 'media',       label: 'Media / Press enquiry' },
  { value: 'data',        label: 'Data partnership'      },
  { value: 'collaborate', label: 'Collaboration'         },
  { value: 'sponsor',     label: 'Sponsorship'           },
  { value: 'other',       label: 'Other'                 },
];

function Field({ label, required, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <label style={LABEL_STYLE}>
        {label}
        {required && <span style={{ color: C.pink, marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

/* ── Success overlay (portal) ─────────────────────────────────────────────── */
function SuccessOverlay({ onClose }) {
  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(6,6,17,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
        animation: 'tnpoll-fadeup 300ms ease',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#0f0f24',
        border: `1px solid rgba(255,255,255,0.1)`,
        borderRadius: 20,
        padding: '3rem 2.5rem',
        maxWidth: 420,
        width: '100%',
        textAlign: 'center',
      }}>
        {/* Icon */}
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'rgba(34,197,94,0.1)',
          border: '2px solid #22c55e',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1.5rem',
        }}>
          <CheckCircle size={28} color="#22c55e" />
        </div>

        <h2 style={{ fontWeight: 800, fontSize: '1.5rem', color: '#22c55e', marginBottom: '0.75rem' }}>
          Thank you!
        </h2>
        <p style={{ fontSize: '0.95rem', color: C.textDim, lineHeight: 1.6, marginBottom: '0.5rem' }}>
          Your message has been received. We'll get back to you soon.
        </p>
        <p style={{ fontSize: '0.82rem', color: C.textDim, opacity: 0.6, fontStyle: 'italic', marginBottom: '2rem', lineHeight: 1.5 }}>
          Expect a minor delay due to a high volume of enquiries. We appreciate your patience.
        </p>

        <button
          onClick={onClose}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(255,255,255,0.06)',
            border: `1px solid rgba(255,255,255,0.1)`,
            borderRadius: 10, padding: '12px 24px',
            fontFamily: "'Poppins', sans-serif",
            fontSize: '0.88rem', fontWeight: 600, color: C.white,
            cursor: 'pointer',
          }}
        >
          <X size={15} />
          Back to site
        </button>
      </div>
    </div>,
    document.body
  );
}

/* ── Main screen ──────────────────────────────────────────────────────────── */
export default function ContactScreen() {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', location: '', reason: '', message: '',
  });
  const [errors, setErrors]     = useState({});
  const [sending, setSending]   = useState(false);
  const [success, setSuccess]   = useState(false);

  // Meta
  useState(() => {
    setMeta({
      title:       'Contact Us | UnofficialElections',
      description: 'Get in touch with the UnofficialElections team — feedback, press enquiries, bug reports, collaborations and more.',
      canonical:   '/contact',
    });
  });

  const set = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: false }));
  };

  const focusStyle  = { borderColor: C.cyan, boxShadow: '0 0 0 3px rgba(0,229,255,0.10)' };
  const errorStyle  = { borderColor: C.pink,  boxShadow: '0 0 0 3px rgba(255,77,158,0.08)' };

  const handleSubmit = async () => {
    // Validate
    const newErrors = {};
    if (!form.name.trim())    newErrors.name    = true;
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = true;
    if (!form.reason)         newErrors.reason  = true;
    if (!form.message.trim()) newErrors.message = true;

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setSending(true);
    try {
      const payload = {
        name:     form.name.trim(),
        phone:    form.phone.trim() || null,
        email:    form.email.trim(),
        location: form.location.trim() || null,
        reason:   form.reason,
        message:  form.message.trim(),
      };

      // 1️⃣ Save to Supabase (permanent record)
      const { error: dbError } = await supabase
        .from('contact_submissions')
        .insert(payload);
      if (dbError) console.error('[contact] Supabase insert error:', dbError);

      // 2️⃣ Email notification via FormSubmit.co (sends to balajirrb@outlook.com)
      //    First submission triggers a one-time activation email to that address.
      await fetch(FORMSUBMIT_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          Name:     payload.name,
          Email:    payload.email,
          Phone:    payload.phone || '—',
          Location: payload.location || '—',
          Reason:   payload.reason,
          Message:  payload.message,
          _subject: `[UnofficialElections] New contact: ${payload.reason} from ${payload.name}`,
          _captcha: 'false',
          // CC all additional recipients (beyond the first)
          ...(NOTIFY_EMAILS.length > 1 && {
            _cc: NOTIFY_EMAILS.slice(1).join(','),
          }),
        }),
      }).catch(e => console.error('[contact] FormSubmit error:', e));

      setSuccess(true);
      setForm({ name: '', phone: '', email: '', location: '', reason: '', message: '' });
    } catch (e) {
      console.error('[contact] submit error:', e);
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="px-5 md:px-10 py-14 md:py-20 max-w-2xl mx-auto">

      {/* Heading */}
      <div style={{ marginBottom: '2.5rem' }}>
        <h1 style={{
          fontWeight: 800, fontSize: 'clamp(2rem, 5vw, 2.8rem)',
          lineHeight: 1.05, letterSpacing: '-0.025em', color: C.white,
          marginBottom: '0.6rem',
        }}>
          Get in{' '}
          <span style={{
            background: `linear-gradient(135deg, ${C.cyan}, ${C.pink})`,
            WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
          }}>
            touch.
          </span>
        </h1>
        <p style={{ fontSize: '1rem', color: C.textDim, lineHeight: 1.65, maxWidth: 480 }}>
          Whether you're a journalist, researcher, developer, or just someone with feedback — we'd love to hear from you.
        </p>
      </div>

      {/* Form */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Name + Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name" required>
            <input
              type="text"
              placeholder="Your full name"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              style={{ ...INPUT_STYLE, ...(errors.name ? errorStyle : {}) }}
              onFocus={e => Object.assign(e.target.style, focusStyle)}
              onBlur={e => { e.target.style.borderColor = errors.name ? C.pink : 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              placeholder="+91 XXXXX XXXXX"
              value={form.phone}
              onChange={e => set('phone', e.target.value)}
              style={INPUT_STYLE}
              onFocus={e => Object.assign(e.target.style, focusStyle)}
              onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
            />
          </Field>
        </div>

        {/* Email */}
        <Field label="Email" required>
          <input
            type="email"
            placeholder="you@email.com"
            value={form.email}
            onChange={e => set('email', e.target.value)}
            style={{ ...INPUT_STYLE, ...(errors.email ? errorStyle : {}) }}
            onFocus={e => Object.assign(e.target.style, focusStyle)}
            onBlur={e => { e.target.style.borderColor = errors.email ? C.pink : 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
          />
        </Field>

        {/* Location + Reason */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Location">
            <input
              type="text"
              placeholder="City, State"
              value={form.location}
              onChange={e => set('location', e.target.value)}
              style={INPUT_STYLE}
              onFocus={e => Object.assign(e.target.style, focusStyle)}
              onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
            />
          </Field>
          <Field label="Reason" required>
            <select
              value={form.reason}
              onChange={e => set('reason', e.target.value)}
              style={{
                ...INPUT_STYLE,
                appearance: 'none',
                cursor: 'pointer',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' fill='none'%3E%3Cpath d='M1 1.5l5 5 5-5' stroke='%235a5a73' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 16px center',
                paddingRight: 40,
                ...(errors.reason ? errorStyle : {}),
              }}
              onFocus={e => Object.assign(e.target.style, focusStyle)}
              onBlur={e => { e.target.style.borderColor = errors.reason ? C.pink : 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
            >
              <option value="" disabled>Select a reason</option>
              {REASONS.map(r => (
                <option key={r.value} value={r.value} style={{ background: '#0f0f24' }}>
                  {r.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* Message */}
        <Field label="Message" required>
          <textarea
            placeholder="Tell us what's on your mind..."
            value={form.message}
            onChange={e => set('message', e.target.value)}
            rows={5}
            style={{
              ...INPUT_STYLE,
              resize: 'vertical',
              lineHeight: 1.55,
              minHeight: 120,
              ...(errors.message ? errorStyle : {}),
            }}
            onFocus={e => Object.assign(e.target.style, focusStyle)}
            onBlur={e => { e.target.style.borderColor = errors.message ? C.pink : 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
          />
        </Field>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={sending}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            background: `linear-gradient(135deg, ${C.cyan}, #0099cc)`,
            border: 'none', borderRadius: 12,
            padding: '15px 32px',
            fontFamily: "'Poppins', sans-serif",
            fontSize: '0.95rem', fontWeight: 700, color: C.white,
            cursor: sending ? 'not-allowed' : 'pointer',
            opacity: sending ? 0.75 : 1,
            width: '100%',
            transition: 'all 250ms ease',
            marginTop: 4,
          }}
        >
          <Send size={17} />
          {sending ? 'Sending…' : 'Send message'}
        </button>

        {/* Privacy note */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 4 }}>
          <Lock size={13} color={C.textDim} style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={{ fontSize: '0.75rem', color: C.textDim, lineHeight: 1.55, opacity: 0.7 }}>
            Your information is only used to respond to your enquiry. We don't share, sell, or use it for any other purpose.
          </p>
        </div>

      </div>

      {success && <SuccessOverlay onClose={() => setSuccess(false)} />}
    </main>
  );
}
